import { FC } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TextField } from 'components/Forms/TextField';
import { SelectField } from 'components/Forms/SelectField';
import { ElectionPicker } from './ElectionPicker';
import { CandidateElections } from './CandidateElections';
import {
  CandidateSubtypeOptions,
  DeStatusOptions,
  RecipientStatusOptions,
  SubtypeOptions,
  TypeOptions,
} from './constants';
import { FormApi } from 'final-form';
import { RecipientFormValues } from './types';
import { required, validateEin } from 'helpers/utils';
import { SwitchToggleField } from 'components/Forms/SwitchToggleField';

interface Props {
  index: number;
  itemVisible: number;
  formState: FormApi<RecipientFormValues>;
  isLoading: boolean;
  isEditMode?: boolean;
  recipientId?: string;
  readOnly?: boolean;
  onDisplayNameBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onTypeChange?: (newType: string) => void;
  onNextTab?: () => void;
}

export const RecipientAccordion: FC<Props> = ({
  index,
  itemVisible,
  formState,
  isLoading,
  isEditMode = false,
  recipientId,
  readOnly = false,
  onDisplayNameBlur,
  onTypeChange,
  onNextTab,
}) => {
  const values = formState.getState().values;
  const isCandidate = values.type === 'CANDIDATE';
  const subtypeOptions = isCandidate ? CandidateSubtypeOptions : SubtypeOptions;

  const handleTypeChange = (newType: string) => {
    if (newType === 'CANDIDATE') {
      formState.change('subtype', CandidateSubtypeOptions[0].value);
      formState.change('recipient_ein', '');
    } else {
      formState.change('subtype', SubtypeOptions[0].value);
    }
    onTypeChange?.(newType);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0 }}
        animate={itemVisible === index ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        className="motion-card-container"
      >
        {index === 0 && (
          <div className="card">
            <div className="card-body card-body-page-detail">
              <TextField
                label="Recipient Display Name *"
                name="recipient_name"
                type="text"
                validate={required}
                customOnBlur={onDisplayNameBlur}
                disabled={readOnly}
              />
              <TextField
                label="Recipient Legal Name *"
                name="recipient_legal_name"
                type="text"
                validate={required}
                disabled={readOnly}
              />
              <div className="row">
                <div className="col-md-4 padding0">
                  <div className="form-input">
                    <label htmlFor="type">Type</label>
                    {isEditMode ? (
                      <p className="oath-body inkBlue">
                        {TypeOptions.find(opt => opt.value === values.type)?.label ?? values.type}
                      </p>
                    ) : (
                      <SelectField
                        name="type"
                        options={TypeOptions}
                        validate={required}
                        onChange={handleTypeChange}
                        disabled={readOnly}
                      />
                    )}
                  </div>
                  {isEditMode && (
                    <div className="form-input">
                      <label>Stripe ID</label>
                      <p className="oath-body inkBlue">{values.stripe_id || '—'}</p>
                    </div>
                  )}
                </div>
                <div className="col-md-2" />
                <div className="col-md-4 padding0">
                  <div className="form-input">
                    <label htmlFor="subtype">Subtype</label>
                    <SelectField
                      name="subtype"
                      options={subtypeOptions}
                      validate={required}
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                {!isCandidate && (
                  <>
                    <div className="col-md-4 padding0">
                      <TextField
                        label="EIN"
                        name="recipient_ein"
                        type="text"
                        placeholder="xxxxxxxxx"
                        validate={validateEin}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-2" />
                  </>
                )}
                <div className="col-md-4 padding0">
                  <TextField
                    label="Photo ID# *"
                    name="recipient_photo_id"
                    type="text"
                    validate={required}
                    disabled={readOnly}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 padding0">
                  <TextField
                    label="Primary Contact Name"
                    name="recipient_contact_name"
                    type="text"
                    disabled={readOnly}
                  />
                </div>
                <div className="col-md-2" />
                <div className="col-md-4 padding0">
                  <TextField
                    label="Primary Contact Email"
                    name="recipient_contact_email"
                    type="text"
                    disabled={readOnly}
                  />
                </div>
              </div>
              <span className="oath-eyebrow inkBlue">Payment types</span>
              <div className="row">
                <div className="col-md-4 padding0">
                  <div className="form-input">
                    <label htmlFor="recipient_status">Recipient Status</label>
                    <SelectField
                      name="recipient_status"
                      options={RecipientStatusOptions}
                      disabled={readOnly}
                    />
                  </div>
                </div>
                <div className="col-md-2" />
                <div className="col-md-4 padding0">
                  <TextField label="Stripe Status" name="stripe_status" type="text" disabled />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 padding0">
                  <div className="row">
                    <div className="col-md-6 padding0">
                      <SwitchToggleField
                        label="Stripe Authorized"
                        name="stripe_authorized"
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-6 padding0">
                      <SwitchToggleField
                        label="Onboarding Form Complete"
                        name="onboarding_form_complete"
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 padding0">
                  <TextField
                    label="DE URL ID *"
                    name="recipient_de_url_id"
                    type="text"
                    placeholder="xxxxxxxxxxxxxx"
                    validate={required}
                    disabled={readOnly}
                  />
                </div>
                <div className="col-md-2" />
                <div className="col-md-4 padding0">
                  <div className="form-input">
                    <label htmlFor="de_status">DE Status</label>
                    {isEditMode ? (
                      <p className="oath-body inkBlue">
                        {DeStatusOptions.find(opt => opt.value === values.de_status)?.label ??
                          values.de_status}
                      </p>
                    ) : (
                      <SelectField name="de_status" options={DeStatusOptions} disabled={readOnly} />
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 padding0">
                  <TextField
                    label="DE API ID"
                    name="recipient_de_api_id"
                    type="text"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    disabled
                  />
                </div>
                <div className="col-md-2" />
                <div className="col-md-4 padding0">
                  <TextField
                    label="Public Fundraising Query String"
                    name="public_fundraising_query_str"
                    type="text"
                    maxLength={100}
                    disabled={readOnly}
                  />
                </div>
              </div>
              <span className="oath-eyebrow inkBlue">Recipient visibility</span>
              <div className="row">
                <div className="col-md-4 padding0">
                  <SwitchToggleField
                    label="Public Search"
                    name="public_search"
                    disabled={readOnly}
                  />
                </div>
              </div>
              {readOnly ? null : isCandidate && !isEditMode ? (
                <button
                  className="btn-inkBlue row submit-button btn btn-secondary"
                  type="button"
                  onClick={onNextTab}
                >
                  Next
                </button>
              ) : (
                <button
                  className="btn-inkBlue row submit-button btn btn-secondary"
                  type="submit"
                  disabled={isLoading}
                >
                  {formState.getState().submitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                      ? 'Update'
                      : 'Create'}
                </button>
              )}
            </div>
          </div>
        )}

        {index === 1 && isCandidate && (
          <div className="card">
            <div className="card-body card-body-page-detail">
              {isEditMode ? (
                recipientId && <CandidateElections recipientId={recipientId} readOnly={readOnly} />
              ) : (
                <ElectionPicker />
              )}
              {!readOnly && (
                <button
                  className="btn-inkBlue row submit-button btn btn-secondary"
                  type="submit"
                  disabled={isLoading}
                >
                  {formState.getState().submitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                      ? 'Update'
                      : 'Create'}
                </button>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
